"use client";

import { useMemo } from "react";
import type { EChartsOption } from "echarts";
import { getEchartsWatermark } from "./echartsWatermark";
import type { ChartEventMarker, ChartFestivalMarker } from "./chartEventMarkers";
import { getYAtTime, buildFestivalScatterSeries } from "./chartEventMarkers";

type ChartDataPoint = [number, number];

const MARKER_COLORS: Record<ChartEventMarker["type"], string> = {
  release: "#66c0f4",
  ea: "#66c0f4",
  topsellers: "#a4d007",
  wishlist: "#beee11",
};

export function useChartOptions(
  chartData: ChartDataPoint[],
  isMobile: boolean,
  primaryColor: string = "#66c0f4",
  isFollowers: boolean = false,
  eventMarkers: ChartEventMarker[] = [],
  valueLabel?: string,
  festivalMarkers: ChartFestivalMarker[] = [],
  /** Якщо true — показуємо тільки дату без часу (для daily-бакетів і followers) */
  dayOnly: boolean = false
): EChartsOption {
  return useMemo(() => {
    const valueLabelText =
      valueLabel ?? (isFollowers ? "Followers" : "Rank");
    const axisColor = "#8f98a0";
    const gridLeft = isMobile ? 32 : 46;
    const gridRight = isMobile ? 16 : 24;
    const gridBottom = isMobile ? 56 : 70;

    // Convert hex color to rgba for filler
    const hexToRgba = (hex: string, alpha: number) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const yValues = chartData.map((d) => d[1]).filter((v) => Number.isFinite(v));
    const yMin = yValues.length ? Math.min(...yValues) : 0;
    const yMax = yValues.length ? Math.max(...yValues) : 1;
    const yRange = Math.max(yMax - yMin, 1);
    // Відступ маркера над лінією: для рангів — більший, щоб не наїжджати на лінію
    const connectorStep = isFollowers
      ? yRange * 0.15
      : Math.max(yRange * 0.35, 5);
    const aboveY = isFollowers ? 1 : -1;

    const markPointData =
      eventMarkers.length > 0
        ? eventMarkers.map((m) => {
            const y = getYAtTime(chartData, m.timestampMs) ?? 0;
            const yMarker = y + aboveY * connectorStep;
            return {
              coord: [m.timestampMs, yMarker] as [number, number],
              name: `${m.tooltipLabel}\n${new Date(m.timestampMs).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}`,
              value: m.label,
              itemStyle: { color: MARKER_COLORS[m.type] },
              label: {
                show: true,
                formatter: m.label,
                color: "#0e141b",
                fontSize: isMobile ? 9 : 10,
                fontWeight: "bold",
              },
            };
          })
        : [];

    const markLineData =
      eventMarkers.length > 0
        ? eventMarkers.map((m) => {
            const y = getYAtTime(chartData, m.timestampMs) ?? 0;
            const yMarker = y + aboveY * connectorStep;
            return [
              { coord: [m.timestampMs, y] as [number, number] },
              { coord: [m.timestampMs, yMarker] as [number, number] },
            ];
          })
        : [];

    return {
      backgroundColor: "transparent",
      animation: false,
      graphic: getEchartsWatermark(),

      tooltip: {
        trigger: "axis",
        backgroundColor: "#11161d",
        borderColor: "#3a4a5a",
        borderWidth: 1,
        padding: [6, 10],
        textStyle: { color: "#cfd8e3", fontSize: 12 },
        axisPointer: {
          type: "line",
          lineStyle: { color: primaryColor, width: 1 },
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (params: any) => {
          const param = Array.isArray(params) ? params[0] : params;
          if (param?.componentSubType === "markPoint" && param?.name) {
            const lines = param.name.split("\n");
            const isClickable = lines[lines.length - 1]?.includes("↗");
            return `
              <div style="font-weight:600;margin-bottom:4px;">${lines[0]}</div>
              ${lines.slice(1, isClickable ? -1 : undefined).map((l: string) => `<div style="color:#8f98a0;font-size:11px;">${l}</div>`).join("")}
              ${isClickable ? `<div style="color:#66c0f4;font-size:11px;margin-top:4px;">${lines[lines.length - 1]}</div>` : ""}
            `;
          }
          const value = param?.value as [number, number] | undefined;
          if (!value) return "";
          const dateValue = new Date(value[0]);
          const showDateOnly = isFollowers || dayOnly;
          const date = showDateOnly
            ? dateValue.toLocaleDateString("en-US", {
                dateStyle: "medium",
              })
            : dateValue.toLocaleString("en-US", {
                dateStyle: "medium",
                timeStyle: "short",
              });
          const dataValue = value[1];
          if (isFollowers) {
            return `
              <div style="font-weight:600;margin-bottom:4px;">${date}</div>
              <div>${valueLabelText}: <strong>${dataValue.toLocaleString(
                "en-US"
              )}</strong></div>
            `;
          }
          return `
            <div style="font-weight:600;margin-bottom:4px;">${date}</div>
            <div>${valueLabelText}: <strong>#${dataValue}</strong></div>
          `;
        },
      },

      grid: {
        left: gridLeft,
        right: gridRight,
        top: 24,
        bottom: gridBottom,
        containLabel: true,
      },

      xAxis: {
        type: "time",
        boundaryGap: [0, 0],
        axisLabel:
          isFollowers || dayOnly
            ? {
                color: axisColor,
                formatter: (value: number) => {
                  return new Date(value).toLocaleDateString("en-US", {
                    dateStyle: "medium",
                  });
                },
              }
            : { color: axisColor },
        axisLine: { lineStyle: { color: "#2a475e" } },
        axisTick: { lineStyle: { color: "#2a475e" } },
        splitLine: {
          show: true,
          lineStyle: { color: "#1b2838", opacity: 0.5 },
        },
      },

      yAxis: {
        type: "value",
        scale: true,
        inverse: !isFollowers,
        minInterval: isFollowers ? undefined : 1,
        min: isFollowers ? undefined : 1,
        axisLabel: {
          color: axisColor,
          formatter: (value: number) => {
            if (value === 0) return "";
            return isFollowers ? value.toLocaleString("en-US") : `#${value}`;
          },
        },
        splitLine: {
          show: true,
          lineStyle: { color: "#2a3a4a", opacity: 0.4 },
        },
        axisLine: { lineStyle: { color: "#2a475e" } },
        axisTick: { lineStyle: { color: "#2a475e" } },
      },

      dataZoom: [
        {
          type: "inside",
          throttle: 70,
        },
        {
          type: "slider",
          height: 26,
          bottom: 26,
          backgroundColor: "#11161d",
          borderColor: "#0e141b",
          dataBackground: {
            lineStyle: { color: "#2f3d4a" },
            areaStyle: { color: "#11161d" },
          },
          fillerColor: hexToRgba(primaryColor, 0.25),
          handleStyle: {
            color: primaryColor,
            borderColor: primaryColor,
            borderWidth: 1,
          },
          textStyle: { color: axisColor, fontSize: 11 },
        },
      ],

      series: [
        {
          name: valueLabelText,
          type: "line",
          smooth: true,
          symbol: "none",
          showSymbol: false,
          connectNulls: true,
          lineStyle: { width: 1.3, color: primaryColor },
          areaStyle: undefined,
          emphasis: { focus: "series" },
          data: chartData,
          ...(markPointData.length > 0 && {
            markPoint: {
              symbol: "roundRect",
              symbolSize: [isMobile ? 32 : 40, isMobile ? 18 : 22],
              data: markPointData,
              animation: false,
              itemStyle: { borderColor: "#16202d", borderWidth: 1 },
            },
          }),
          ...(markLineData.length > 0 && {
            markLine: {
              symbol: ["none", "none"],
              animation: false,
              lineStyle: { color: "#5a6b7a", width: 1, type: "solid" },
              data: markLineData,
            },
          }),
        },
        ...(buildFestivalScatterSeries(festivalMarkers, chartData, isMobile)
          ? [buildFestivalScatterSeries(festivalMarkers, chartData, isMobile)]
          : []),
      ],
    } as EChartsOption;
  }, [chartData, isMobile, primaryColor, isFollowers, eventMarkers, valueLabel, festivalMarkers, dayOnly]);
}
